import './antModal.less';

import { Observer } from 'mobx-react';
import React, { CSSProperties, MutableRefObject, useCallback, useEffect, useState } from 'react';
import { Modal, ModalProps } from 'antd';
import { ControlPosition, DraggableCore, DraggableData, DraggableEvent } from 'react-draggable';

import { State } from 'tools/state';
import { ResizableBox, ResizeCallbackData, ResizeHandle } from 'react-resizable';
import { createRoot, Root } from 'react-dom/client';
import { Toolbar } from 'controls/react/layout/toolbar';
import { newGuid } from 'tools/guid';
import classNames from 'classnames';
import { getTopLeftPagePosition } from '../../modalWindow';
import { CssVariables } from 'styles/cssVariables';
import { useTriggerContext } from 'controls/react/ant/utils';
import { TriggerContext } from './utils';
import { AntButton } from 'controls/react/ant/antButton';
import { Size } from 're-resizable';
import { mouseCurrentPosition } from 'framework/mouse';
import b_ from 'b_';

import { i18n } from 'core/localization';
import { Theme } from 'framework/theme/types';
import { ThemeScope } from 'framework/theme/components/theme-scope/themeScope';

export enum ModalPosition {
	Centered = 'centered',
	TopLeft = 'topLeft',
	Default = 'default',
	TopRight = 'topRight',
	Mouse = 'mouse',
	Anchor = 'anchor',
	Custom = 'custom',
}

export type SetModalPositionCallback = (styles: CSSProperties) => void;

// ModalProps['styles'] is an object-or-function union (antd's semantic-styles machinery); nothing
// in this app ever passes a function, so this component's own prop narrows to the plain object shape.
type ModalStyles = {
	root?: React.CSSProperties;
	header?: React.CSSProperties;
	body?: React.CSSProperties;
	footer?: React.CSSProperties;
	container?: React.CSSProperties;
	title?: React.CSSProperties;
	wrapper?: React.CSSProperties;
	mask?: React.CSSProperties;
	close?: React.CSSProperties;
};

export type AntModalProps = Omit<
	ModalProps,
	'centered' | 'destroyOnClose' | 'maskClosable' | 'focusTriggerAfterClose' | 'styles'
> & {
	styles?: ModalStyles;
	mode?: 'update' | 'create';
	positionType?: ModalPosition | SetModalPositionCallback;
	width?: number;
	draggable?: boolean;
	height?: number;
	draggableHandle?: string;
	draggableExclusion?: string;
	resizable?: boolean;
	defaultFooterButtons?: ('ok' | 'cancel')[];
	cancelText?: string;
	positionAnchor?: React.MutableRefObject<HTMLElement> | HTMLElement;
	closeOn?: 'clickOutside';
	contentDivRef?: React.MutableRefObject<HTMLDivElement>;
	onResize?: (size: Size) => void;
	minWidth?: number;
	// without a mask, a click outside the modal reaches what is under it instead of closing the modal
	clickThrough?: boolean;
};

type AntModalPropsInternal = AntModalProps & {
	id: string;
	customToolbarSet?: boolean;
	onResize?: (size: { height: number; width: number }) => void;
};

const defaultAntModalWidth = 520;

const b = b_.with('ant-modal-wrapper');
const resizableB = b_.with('react-resizable');

export const AntModal: React.FunctionComponent<AntModalProps> = (propsInitial) => {
	const { context, subPopupElements } = useTriggerContext();
	let props = useDefaultValues(propsInitial);
	props = useFixedHeight(props);
	props = useInitialModalPosition(props);
	props = useOnScreenPosition(props);
	props = useCustomCloseConditions(props, subPopupElements);
	props = useResizableDraggableRenderer(props);
	props = useCreateUpdateMode(props);
	props = useDefaultFooter(props);
	props = useZIndexManager(props);

	const contentClassNames = b('content', {
		draggable: props.draggable,
	});

	const { title, children, rootClassName, clickThrough, ...rest } = props;

	const rootClassNameDerived = classNames(
		b({
			'custom-footer': props.customToolbarSet !== false,
			'click-through': clickThrough && !props.mask,
		}),
		rootClassName,
	);

	return (
		<TriggerContext.Provider value={context}>
			<Modal rootClassName={rootClassNameDerived} {...rest}>
				<div ref={props.contentDivRef} className={contentClassNames} id={props.id}>
					{title &&
						(React.isValidElement(title) && title.type === Toolbar ? title : <Toolbar title={title} />)}
					{children}
				</div>
			</Modal>
		</TriggerContext.Provider>
	);
};

type OpenModalProps = Omit<AntModalProps, 'open' | 'onOk'> & {
	onOk?: (e: React.MouseEvent<HTMLButtonElement>) => Promise<boolean | void> | boolean | void;
	theme?: Theme | null;
};

function useDefaultValues(props: AntModalProps): AntModalPropsInternal {
	const [id] = React.useState(() => newGuid());
	const contentDivRef = React.useRef<HTMLDivElement>(null);

	const { open, draggable, resizable, focusable, ...rest } = props;

	return {
		id,
		contentDivRef,
		open: open === undefined || open === null ? true : open,
		draggable: draggable === undefined || draggable === null ? true : draggable,
		resizable: resizable === undefined || resizable === null ? true : resizable,
		// antd traps focus only in modals with a mask, so a maskless (draggable) modal opened over a masked one
		// leaves the lower modal's trap active and every focus inside the upper one is pulled back down
		focusable: { trap: false, ...focusable },
		...rest,
	};
}

function useZIndexManager(props: AntModalPropsInternal) {
	const [zIndex, setZIndex] = React.useState<number | null>(null);
	const zIndexRef = React.useRef<number | null>(null);

	const updateZIndex = useCallback(() => {
		if (props.zIndex && !isNaN(props.zIndex)) {
			setZIndex(props.zIndex);
		} else {
			if (!zIndexRef.current || CssVariables.currentZIndex > zIndexRef.current) {
				CssVariables.currentZIndex++;
				zIndexRef.current = CssVariables.currentZIndex;
				setZIndex(CssVariables.currentZIndex);
			}
		}
	}, [props.zIndex]);

	const onClick = React.useCallback(
		(e: MouseEvent) => {
			if (!props.contentDivRef.current?.contains(e.target as Node)) return;
			updateZIndex();
		},
		[updateZIndex],
	);

	React.useEffect(() => {
		document.addEventListener('click', onClick, true);
		return () => document.removeEventListener('click', onClick, true);
	}, [onClick]);

	React.useEffect(() => {
		updateZIndex();
	}, []);

	return { ...props, zIndex };
}

function useCreateUpdateMode(props: AntModalPropsInternal) {
	const { okText, cancelText, ...rest } = props;
	if (props.okText === undefined) {
		props.okText = props.mode == 'update' ? i18n('Update') : i18n('Create');
	}
	if (props.cancelText === undefined) {
		props.cancelText = i18n('Cancel');
	}

	return {
		...rest,
		okText: okText ?? (props.mode == 'update' ? i18n('Update') : i18n('Create')),
		cancelText: cancelText ?? i18n('Cancel'),
	};
}

function useDefaultFooter(props: AntModalPropsInternal) {
	if (props.footer !== undefined) return props;

	//ant modal comes with cancel/ok buttons but we use ok/cancel in the app so we swap them here
	const footer = [];
	if (!props.defaultFooterButtons || props.defaultFooterButtons.includes('ok')) {
		footer.push(
			<AntButton onClick={props.onOk} type={'primary'} key={'ok'} {...props.okButtonProps}>
				{props.okText}
			</AntButton>,
		);
	}
	if (!props.defaultFooterButtons || props.defaultFooterButtons.includes('cancel')) {
		footer.push(
			<AntButton
				onClick={(e: React.MouseEvent<HTMLButtonElement>) => props.onCancel?.(e)}
				key={'cancel'}
				{...props.cancelButtonProps}
			>
				{props.cancelText}
			</AntButton>,
		);
	}

	return {
		...props,
		customToolbarSet: false,
		footer,
	};
}

export function useResizableDraggableRenderer(props: AntModalPropsInternal) {
	const [size, setSize] = useState(() => ({ height: props.height, width: props.width }));
	// position extracted to the upper level from useDraggable to make movement smoother, otherwise size changes with jumps
	const [position, setPosition] = useState(() => ({ x: 0, y: 0 }));

	const { modalRender, mask, resizable, ...rest } = props;

	const draggableRef = React.useRef<HTMLDivElement>(null);

	const { wrapDraggable, bounds, draggable } = useDraggable(props, draggableRef, [position, setPosition]);

	const wrapResizable = useResizable(props, [size, setSize], [position, setPosition]);

	useEffect(() => {
		if (size.width != props.width || size.height != props.height) {
			setSize({ height: props.height, width: props.width });
		}
	}, [props.width, props.height]);

	return {
		...rest,
		resizable,
		draggable,
		width: size.width,
		mask: draggable && mask === undefined ? false : mask,
		modalRender: React.useMemo(() => {
			if (!draggable && !resizable) {
				return null;
			}
			return (modal: React.ReactNode) => {
				return wrapDraggable(
					wrapResizable(
						<div
							ref={draggableRef}
							className={b('wrapper')}
							style={draggable ? { transform: `translate(${position.x}px, ${position.y}px)` } : undefined}
						>
							{modalRender?.(modal) ?? modal}
						</div>,
					),
				);
			};
		}, [
			draggable,
			resizable,
			bounds,
			size.width,
			size.height,
			position.x,
			position.y,
			wrapResizable,
			wrapDraggable,
			modalRender,
		]),
	};
}

export function useInitialModalPosition(props: AntModalPropsInternal) {
	const { style, positionType, ...others } = props;
	const positionTypeActual: ModalPosition | SetModalPositionCallback =
		positionType ?? (props.height && props.width ? ModalPosition.Custom : ModalPosition.Default);

	const styleEffective = React.useMemo(() => {
		if (
			typeof positionTypeActual != 'function' &&
			(positionTypeActual == ModalPosition.Default || positionTypeActual == ModalPosition.Centered)
		) {
			return style;
		}

		const result: React.CSSProperties = {
			...(style ?? {}),
		};

		if (typeof positionTypeActual == 'function') {
			positionTypeActual(result);
		} else {
			const topLeftPosition = getTopLeftPagePosition();
			if (positionTypeActual == ModalPosition.TopLeft) {
				result.top = topLeftPosition.top;
				result.left = topLeftPosition.left;
			} else if (positionTypeActual == ModalPosition.TopRight) {
				result.top = topLeftPosition.top;
				result.left = window.innerWidth - topLeftPosition.left - (others.width ?? defaultAntModalWidth);
			} else if (positionTypeActual == ModalPosition.Mouse) {
				result.top = Math.max(mouseCurrentPosition.top, topLeftPosition.top);
				result.left = mouseCurrentPosition.left;
			} else if (positionTypeActual == ModalPosition.Anchor && others.positionAnchor) {
				const target =
					'current' in others.positionAnchor ? others.positionAnchor.current : others.positionAnchor;
				const rect = target.getBoundingClientRect();
				result.top = rect.top + rect.height;
				result.left = rect.left + rect.width;
			} else if (positionTypeActual == ModalPosition.Custom) {
				result.top = (window.innerHeight - props.height) / 2;
				result.left = (window.innerWidth - props.width) / 2;
			}
		}

		result.margin = '0';

		return result;
	}, []);

	return {
		...others,
		style: styleEffective,
		centered: positionTypeActual == ModalPosition.Centered,
	};
}

function useCustomCloseConditions(props: AntModalPropsInternal, popups: Record<string, HTMLElement>) {
	React.useEffect(() => {
		if (!props.closeOn || !props.open) return;

		const onClick = (e: MouseEvent) => {
			if (!props.contentDivRef?.current) return;

			if (e.target instanceof HTMLElement) {
				let clickOnWindow: boolean;
				const target = e.target;

				if (props.contentDivRef.current.closest('.ant-modal').contains(target)) {
					clickOnWindow = true;
				} else {
					clickOnWindow = Object.values(popups).some((x) => {
						return !!x && (x == target || x.contains(target));
					});
				}

				if (!clickOnWindow) {
					props.onCancel(null);
				}
			}
		};

		const tuple = {
			onClick,
			destroyed: false,
		};

		requestIdleCallback(() => {
			if (tuple.destroyed) return;

			// captured, as a click that something outside stops propagating is still a click outside
			document.addEventListener('click', tuple.onClick, true);
		});

		return () => {
			document.removeEventListener('click', tuple.onClick, true);
			tuple.destroyed = true;
		};
	}, [props.onCancel, props.contentDivRef, props.closeOn, props.open]);

	return props;
}

// Modals positioned via explicit top/left (anchored, mouse-relative, etc.) must never end up flush
// against - or worse, partially past - a screen edge. Anything closer than this is pulled back in.
const MODAL_SCREEN_EDGE_MARGIN = 100;

function useOnScreenPosition(props: AntModalPropsInternal) {
	const { style, width, height, ...others } = props;

	const positioning = React.useMemo(() => {
		const result = {
			style: style,
			width: width,
			height: height,
		};

		if (window.innerWidth < props.width) {
			result.width = window.innerWidth - 20;
		}

		if (window.innerHeight < props.height) {
			result.height = window.innerHeight - 20;
		}

		if (style == null || (typeof style.top != 'number' && typeof style.left != 'number')) return result;

		const footerHeight = () => {
			if (props.footer === undefined || props.footer === null) {
				return 0;
			}
			return 60;
		};

		if (typeof result.style.left == 'number') {
			if (result.style.left < MODAL_SCREEN_EDGE_MARGIN) {
				result.style.left = MODAL_SCREEN_EDGE_MARGIN;
			} else if (window.innerWidth - MODAL_SCREEN_EDGE_MARGIN < result.width + result.style.left) {
				result.style.left = window.innerWidth - MODAL_SCREEN_EDGE_MARGIN - result.width;
			}
		}

		if (typeof result.style.top == 'number') {
			if (result.style.top < MODAL_SCREEN_EDGE_MARGIN) {
				result.style.top = MODAL_SCREEN_EDGE_MARGIN;
			} else if (window.innerHeight - MODAL_SCREEN_EDGE_MARGIN < result.height + result.style.top) {
				result.style.top = window.innerHeight - MODAL_SCREEN_EDGE_MARGIN - result.height - footerHeight();
			}
		}

		return result;
	}, [props.style, props.width, props.height]);

	// Content-driven modals (no explicit height, e.g. the color picker) don't know their real height at
	// the point the memo above runs, so its bottom-edge clamp is a no-op for them. offsetHeight/
	// ResizeObserver box sizes ignore CSS transforms, so measuring the rendered .ant-modal this way stays
	// correct through the antd enter animation and react-draggable's positioning transform alike.
	const [bottomShift, setBottomShift] = React.useState(0);

	React.useEffect(() => {
		if (height != null) return;
		if (positioning.style == null || typeof positioning.style.top != 'number') return;

		const footerH = props.footer !== undefined && props.footer !== null ? 60 : 0;
		const idealTop = positioning.style.top;

		const recompute = (modalEl: HTMLElement) => {
			const maxTop = window.innerHeight - MODAL_SCREEN_EDGE_MARGIN - modalEl.offsetHeight - footerH;
			const clampedTop = Math.min(idealTop, Math.max(maxTop, MODAL_SCREEN_EDGE_MARGIN));
			setBottomShift(idealTop - clampedTop);
		};

		// contentDivRef isn't attached to a mounted .ant-modal yet on the render where this effect first
		// fires - antd's dialog defers actually mounting the panel by a tick while its enter motion spins
		// up. Poll with a short timer until it shows up rather than silently giving up (requestAnimationFrame
		// is not a safe substitute here: it can stop firing while the tab isn't considered visible/focused).
		let timeoutId: number | null = null;
		let observer: ResizeObserver | null = null;
		let cancelled = false;

		const tryAttach = () => {
			if (cancelled) return;
			const modalEl = others.contentDivRef?.current?.closest<HTMLElement>('.ant-modal');
			if (!modalEl) {
				timeoutId = window.setTimeout(tryAttach, 16);
				return;
			}
			recompute(modalEl);
			observer = new ResizeObserver(() => recompute(modalEl));
			observer.observe(modalEl);
		};

		tryAttach();

		return () => {
			cancelled = true;
			if (timeoutId != null) window.clearTimeout(timeoutId);
			observer?.disconnect();
		};
	}, [height, positioning.style, others.contentDivRef, props.footer]);

	const finalStyle = React.useMemo(() => {
		if (bottomShift === 0 || positioning.style == null || typeof positioning.style.top != 'number') {
			return positioning.style;
		}
		return { ...positioning.style, top: positioning.style.top - bottomShift };
	}, [positioning.style, bottomShift]);

	return {
		...others,
		style: finalStyle,
		width: positioning.width,
		height: positioning.height,
	};
}

const CustomResizeHandle = (props: { resizeHandle: ResizeHandle; innerRef: React.RefObject<HTMLElement> }) => {
	const { resizeHandle, innerRef, ...others } = props;
	const modifier: { [id: string]: boolean } = {};
	modifier[resizeHandle] = true;
	return <span className={resizableB('resize-handle', modifier)} ref={innerRef} {...others} />;
};

export function useResizable(
	props: AntModalPropsInternal,
	sizeState: [
		{ height: number; width: number },
		React.Dispatch<React.SetStateAction<{ height: number; width: number }>>,
	],
	positionState: [ControlPosition, React.Dispatch<React.SetStateAction<{ x: number; y: number }>>],
) {
	const { resizable } = props;
	const [{ width, height }, setSize] = sizeState;
	const [position, setPosition] = positionState;
	const minConstraints: [width: number, height: number] | undefined = props.minWidth
		? [props.minWidth, 0]
		: undefined;

	// Condition with positionType needed because if position centered modal uses auto margin, and we need to fix position
	const onResize = React.useCallback(
		(e: React.SyntheticEvent, data: ResizeCallbackData) => {
			const size = { height: data.size.height, width: data.size.width };
			if (['n', 'nw', 'w', 'sw'].includes(data.handle)) {
				let movementX = width - data.size.width;
				const movementY = height - data.size.height;
				if (props.positionType == ModalPosition.Default || props.positionType == ModalPosition.Centered) {
					movementX = movementX / 2;
				}
				const newPosition =
					data.handle == 'sw'
						? { x: position.x + movementX, y: position.y }
						: { x: position.x + movementX, y: position.y + movementY };
				setPosition(newPosition);
			}
			if (
				['e', 'se'].includes(data.handle) &&
				(props.positionType == ModalPosition.Default || props.positionType == ModalPosition.Centered)
			) {
				const movementX = (width - data.size.width) / 2;
				const newPosition = { x: position.x - movementX, y: position.y };
				setPosition(newPosition);
			}
			setSize(size);
			props.onResize?.(size);
		},
		[position.x, position.y, width, height],
	);

	return React.useMemo(
		() => (content: React.ReactNode) => {
			if (!resizable) {
				return content;
			}
			return (
				<div>
					<ResizableBox
						width={width ?? 0}
						height={height}
						resizeHandles={['se', 'e', 's', 'ne', 'nw', 'w', 'n', 'sw']}
						handle={(resizeHandle, ref) => (
							<CustomResizeHandle resizeHandle={resizeHandle} innerRef={ref} />
						)}
						onResize={onResize}
						minConstraints={minConstraints}
					>
						{content}
					</ResizableBox>
				</div>
			);
		},
		[resizable, width, height, position.x, position.y],
	);
}

export function useDraggable(
	props: AntModalPropsInternal,
	draggableRef: MutableRefObject<HTMLDivElement>,
	positionState: [ControlPosition, React.Dispatch<React.SetStateAction<ControlPosition>>],
) {
	const [bounds, setBounds] = React.useState(() => ({ left: 0, top: 0, bottom: 0, right: 0 }));
	let { draggable, draggableHandle, draggableExclusion } = props;
	const [position, setPosition] = positionState;

	draggable ??= true;
	draggableHandle ??= `.ant-modal-wrapper__content_draggable[id="${props.id}"] > .section > .toolbar, .ant-modal-wrapper__content_draggable[id="${props.id}"] > .toolbar`;
	draggableExclusion ??= 'input';

	// react-draggable's own <Draggable> accumulates position via its internal `this.state.x/y`,
	// updated through React's setState. Under React 18's automatic batching, several native
	// `mousemove` events dispatched within the same render tick (routine with a fast/smooth drag)
	// all read that same not-yet-committed state, so only the last event's delta survives instead
	// of the sum of all of them - movement gets dropped, and the element visibly lags the cursor
	// then jumps to catch up. DraggableCore instead reports each event's delta from its own plain
	// instance field (not React state), so it can't go stale mid-batch; we accumulate those deltas
	// ourselves in a ref and write the transform straight to the DOM, bypassing React entirely
	// until the drag ends - onStop is the only point that commits the final position to state.
	const positionRef = React.useRef(position);
	const boundsRef = React.useRef(bounds);
	boundsRef.current = bounds;

	const onStart = React.useCallback(() => {
		const { clientWidth, clientHeight } = window.document.documentElement;
		const targetRect = draggableRef.current?.getBoundingClientRect();
		if (!targetRect) {
			return;
		}

		positionRef.current = position;

		setBounds({
			left: -targetRect.left + position.x,
			right: clientWidth - (targetRect.right - position.x),
			top: -targetRect.top + position.y,
			bottom: clientHeight - (targetRect.bottom - position.y),
		});
	}, [position.x, position.y]);

	const onDrag = React.useCallback((_event: DraggableEvent, coreData: DraggableData) => {
		const b = boundsRef.current;
		const x = Math.min(Math.max(positionRef.current.x + coreData.deltaX, b.left), b.right);
		const y = Math.min(Math.max(positionRef.current.y + coreData.deltaY, b.top), b.bottom);
		positionRef.current = { x, y };
		if (draggableRef.current) {
			draggableRef.current.style.transform = `translate(${x}px, ${y}px)`;
		}
	}, []);

	const onStop = React.useCallback(() => {
		setPosition(positionRef.current);
	}, []);

	return {
		wrapDraggable: React.useMemo(
			() => (content: React.ReactNode) => {
				if (!draggable) {
					return content;
				}
				return (
					<DraggableCore
						// React 19 removed ReactDOM.findDOMNode, which react-draggable used internally to
						// locate its child's DOM node - without nodeRef it throws/crashes on mount. draggableRef
						// already points directly at the div carrying the position transform (set as an inline
						// style bound to `position` state in useResizableDraggableRenderer), and onDrag below
						// writes to that same node's style during an active drag.
						nodeRef={draggableRef}
						handle={draggableHandle}
						cancel={draggableExclusion}
						onStart={onStart}
						onDrag={onDrag}
						onStop={onStop}
					>
						{content}
					</DraggableCore>
				);
			},
			[draggable, draggableHandle, draggableExclusion, onStart, onDrag, onStop],
		),
		bounds,
		draggable,
	};
}

export function useFixedHeight(props: AntModalPropsInternal) {
	const { styles, ...rest } = props;
	const { body, ...otherStyles } = styles ?? {};

	const bodyStylesUpdated = React.useMemo(() => {
		if (props.height == null || props.resizable) {
			return styles?.body;
		}

		const result: React.CSSProperties = {
			...(body ?? {}),
		};

		result.height = props.height + 'px';

		return result;
	}, [styles?.body, props.height, props.resizable]);

	return {
		styles: {
			body: bodyStylesUpdated,
			...otherStyles,
		},
		...rest,
	};
}

export type ModalDescription = {
	close: (resolved?: boolean) => void;
	root: Root;
	promise: Promise<boolean>;
};

export function openModal(props: (() => OpenModalProps) | OpenModalProps, content: React.ReactNode): ModalDescription {
	const [root, destroyContainer] = createContainer();

	let resolveHolder: (value: boolean) => void;

	const getProps = (p: (() => OpenModalProps) | OpenModalProps) => {
		if (typeof p == 'function') {
			return p();
		}
		return p;
	};

	let unsubscribeNavigation: () => void;

	const closeHandler = (resolved?: boolean) => {
		unsubscribeNavigation();
		destroyContainer();
		resolveHolder(resolved ?? false);
	};

	const onCancelWrapper = (e?: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLElement>) => {
		getProps(props).onCancel?.(e);
		closeHandler();
	};

	const onOkWrapper = async (e: React.MouseEvent<HTMLButtonElement>) => {
		const { onOk } = getProps(props);
		if (onOk) {
			try {
				const result = await onOk(e);
				resolveHolder(typeof result == 'boolean' ? result : true);
			} catch {
				resolveHolder(false);
			}
		}
		if (!e.isDefaultPrevented()) {
			unsubscribeNavigation();
			root.unmount();
			resolveHolder(true);
		}
	};

	const promise = new Promise<boolean>((resolve) => {
		resolveHolder = resolve;

		root.render(
			<>
				<Observer>
					{() => {
						const { onOk, onCancel, theme, ...rest } = getProps(props);
						const modal = (
							<AntModal onOk={onOkWrapper} onCancel={onCancelWrapper} open={true} {...rest}>
								{content}
							</AntModal>
						);
						return theme ? <ThemeScope theme={theme}>{modal}</ThemeScope> : modal;
					}}
				</Observer>
			</>,
		);

		unsubscribeNavigation = State.mainApp?.registerForOnNavigate(() => onCancelWrapper());
	});

	return {
		promise,
		root,
		close: closeHandler,
	};
}

export function createContainer(renderTo = document.body) {
	const container = document.createElement('div');
	renderTo.appendChild(container);

	const root = createRoot(container);

	let shouldUnmount = true;
	const unmountOnce = () => {
		if (shouldUnmount) {
			root.unmount();
			shouldUnmount = false;
		}
	};

	return [root, unmountOnce, container] as const;
}

type RenderIntoContainerOptions = {
	unmountOnNavigate?: boolean;
};

type RenderIntoContainerCallbackArgs = {
	root: Root;
	container: HTMLDivElement;
	unmountOnce: () => void;
};

export function renderIntoContainer(
	callback: (args: RenderIntoContainerCallbackArgs) => void,
	options?: RenderIntoContainerOptions,
) {
	const container = document.createElement('div');
	document.body.appendChild(container);

	const root = createRoot(container);

	let shouldUnmount = true;
	const unmountOnce = () => {
		if (shouldUnmount) {
			root.unmount();
			shouldUnmount = false;
		}
	};

	if (options?.unmountOnNavigate !== false) {
		State.mainApp?.registerForOnNavigate(() => {
			unmountOnce();
		});
	}
	callback({ root, container, unmountOnce });

	return [root, container, unmountOnce] as const;
}

export function createContainerRoot() {
	return createContainer()[0];
}
